import React, { useEffect, useRef, useState } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import './EmailForm.css';
import emailDict from './email.json';
import { sendContactMessage } from '@/lib/contactApi';
import company from '@/content/company.json';
import { contactStore } from '@/lib/variables';

export default function EmailForm() {
    const { lang } = useI18n();
    const normalized = lang.toUpperCase() as keyof typeof emailDict;
    const content = emailDict[normalized];

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<'idle'|'sending'|'sent'|'error'>('idle');
    // Anti-spam: a field people never see, and when the form first rendered.
    const [website, setWebsite] = useState('');
    const startedAt = useRef(Date.now());
    // Who the confirmation thanks; kept apart from the (cleared) inputs.
    const [sentTo, setSentTo] = useState({ name: '', email: '' });
    const resultRef = useRef<HTMLDivElement>(null);

    // "Ask about this service" (services list, NISG page, announcement) fills
    // subject and message, whether the form is already on screen or mounts later.
    useEffect(() => {
        const apply = (p: { subject: string; message: string }) => {
            setSubject(p.subject);
            setMessage(p.message);
            setStatus('idle');
        };
        const pending = contactStore.take();
        if (pending) apply(pending);
        return contactStore.on(apply);
    }, []);

    // The outcome replaces the form (or sits above the button), right where
    // the visitor is looking — a corner toast was easy to miss. Bring it into
    // view and focus it so screen readers announce it too.
    useEffect(() => {
        if (status !== 'sent' && status !== 'error') return;
        const el = resultRef.current;
        el?.focus({ preventScroll: true });
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, [status]);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setStatus('sending');

        try {
            const result = await sendContactMessage({
                name,
                email,
                subject,
                message,
                lang: normalized,
                website,
                startedAt: startedAt.current,
            });

            if (result.success) {
                setSentTo({ name: name.trim(), email: email.trim() });
                setName('');
                setEmail('');
                setSubject('');
                setMessage('');
                setStatus('sent');
            } else {
                throw new Error(result.error);
            }
        } catch (err) {
            console.error('Contact form error:', err);
            setStatus('error');
        }
    }

    if (status === 'sent') {
        const firstName = sentTo.name.split(/\s+/)[0];
        return (
            <div ref={resultRef} className="email-form__result email-form__result--ok" role="status" tabIndex={-1}>
                <CircleCheck className="email-form__result-icon" size={28} strokeWidth={1.75} aria-hidden />
                <div>
                    <p className="email-form__result-title">{content.sentTitle}</p>
                    <p className="email-form__result-body">
                        {content.sentBody
                            .replace('{name}', firstName ? `, ${firstName}` : '')
                            .replace('{email}', sentTo.email)}
                    </p>
                    <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => {
                            startedAt.current = Date.now();
                            setStatus('idle');
                        }}
                    >
                        {content.sendAnother}
                    </button>
                </div>
            </div>
        );
    }

    const errorMessage = content.error
        .replace('{whatsapp}', company.whatsapp)
        .replace('{email}', company.mail);

    return (
        <form onSubmit={submit} className="email-form">
            <div className="row">
                <div className="field">
                    <label>{content.name}</label>
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="field">
                    <label>{content.email}</label>
                    <input
                        type="email"
                        className="input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
            </div>

            <div className="field">
                <label>{content.subject}</label>
                <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required />
            </div>

            <div className="field">
                <label>{content.yourMessage}</label>
                <textarea
                    className="textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                />
            </div>

            {/* Honeypot: off-screen and skipped by keyboard and screen readers. */}
            <div className="email-form__hp" aria-hidden="true">
                <label>
                    Website
                    <input
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                    />
                </label>
            </div>

            {status === 'error' && (
                <div ref={resultRef} className="email-form__result email-form__result--error" role="alert" tabIndex={-1}>
                    <CircleAlert className="email-form__result-icon" size={22} strokeWidth={1.75} aria-hidden />
                    <p className="email-form__result-body">{errorMessage}</p>
                </div>
            )}

            <button className="button-primary" disabled={status === 'sending'}>
                {status === 'sending' ? '...' : content.send}
            </button>
        </form>

    );
}
